import { Plane } from 'lucide-react';
import {
  CABIN_KEYS,
  DEFAULT_AIRPORT,
  airportsOf,
  bagCoverOf,
  JET_KINDS,
  bringAlongOf,
  chopperOf,
  privateJetsOf,
  scriptOf,
  type AirportContent,
  type AirportStop,
  type Destination,
} from '@london-life/contracts';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Textarea } from '@london-life/ui/components/textarea';
import { Switch } from '@london-life/ui/components/switch';
import { PageHeader } from '@london-life/ui/admin/page-header';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import { useAdmin } from '../../lib/admin-context';
import { useContent } from './use-content';
import { ContentBar } from './versions';
import { Field, number, toPence, idFor } from './form';

// The airport (as Lagos Life's): the game's airline, the cabins and what they cost compared with economy, the
// cities the airline will fly to and their fares, and peak hours. Part of the content draft, published with it.
// Every destination "opens soon" until its city is built.
export function AirportPage() {
  const { can } = useAdmin();
  const editor = useContent();
  const working = editor.working;
  const readOnly = !can('content.write');
  const airport = working?.airport ?? DEFAULT_AIRPORT;
  const change = (fn: (draft: AirportContent) => void) =>
    editor.edit((content) => {
      content.airport ??= structuredClone(DEFAULT_AIRPORT);
      fn(content.airport);
    });
  // the game's airports, the bag cover and the lines on board (content made before them starts from the defaults)
  const stop = (placeId: string, fn: (item: AirportStop) => void) =>
    change((draft) => {
      draft.airports ??= structuredClone(airportsOf(draft));
      const item = draft.airports.find((entry) => entry.placeId === placeId);
      if (item) fn(item);
    });
  const cover = bagCoverOf(airport);
  const script = scriptOf(airport);
  const along = bringAlongOf(airport);
  const changeAlong = (fn: (draft: NonNullable<AirportContent['bringAlong']>) => void) =>
    change((draft) => {
      draft.bringAlong ??= structuredClone(bringAlongOf(draft));
      fn(draft.bringAlong);
    });
  const jets = privateJetsOf(airport);
  const changeJets = (fn: (draft: NonNullable<AirportContent['privateJets']>) => void) =>
    change((draft) => {
      draft.privateJets ??= structuredClone(privateJetsOf(draft));
      fn(draft.privateJets);
    });
  const chopper = chopperOf(airport);
  const changeChopper = (fn: (draft: NonNullable<AirportContent['chopper']>) => void) =>
    change((draft) => {
      draft.chopper ??= structuredClone(chopperOf(draft));
      fn(draft.chopper);
    });
  const changeCover = (fn: (draft: NonNullable<AirportContent['bagCover']>) => void) =>
    change((draft) => {
      draft.bagCover ??= structuredClone(bagCoverOf(draft));
      fn(draft.bagCover);
    });
  const changeScript = (fn: (draft: NonNullable<AirportContent['script']>) => void) =>
    change((draft) => {
      draft.script ??= structuredClone(scriptOf(draft));
      fn(draft.script);
    });
  const destination = (id: string, fn: (item: Destination) => void) =>
    change((draft) => {
      const item = draft.destinations.find((entry) => entry.id === id);
      if (item) fn(item);
    });
  return (
    <section>
      <PageHeader
        title="Airport"
        description="The airports' Departures (as Lagos Life's): the airline, the cabins, the game's own airports flown between (London City, Heathrow, Gatwick) and their fares, the bag cover, what is said on board, and the cities that open soon. Changes are a draft until they are published."
      />
      <ContentBar editor={editor} />
      {editor.failure && <ErrorNotice error={editor.failure} onRetry={() => void editor.reload()} />}
      {!working && !editor.failure && <p className="admin-muted">Loading…</p>}
      {working && (
        <div className="content-editor">
          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Airline">
            <h2 className="content-subtitle">
              <Plane size={16} aria-hidden="true" /> Airline
            </h2>
            <div className="content-grid">
              <Field id="air-name" label="Name">
                <Input
                  id="air-name"
                  value={airport.airline.name}
                  maxLength={40}
                  onChange={(e) => change((draft) => void (draft.airline.name = e.target.value))}
                />
              </Field>
              <Field id="air-code" label="Code (two letters)">
                <Input
                  id="air-code"
                  value={airport.airline.code}
                  maxLength={2}
                  onChange={(e) => change((draft) => void (draft.airline.code = e.target.value.toUpperCase()))}
                />
              </Field>
              {(['body', 'tail', 'stripe'] as const).map((part) => (
                <Field key={part} id={`air-${part}`} label={`Livery: ${part}`}>
                  <Input
                    id={`air-${part}`}
                    type="color"
                    value={airport.airline.colours[part]}
                    onChange={(e) => change((draft) => void (draft.airline.colours[part] = e.target.value))}
                  />
                </Field>
              ))}
              <Field id="air-fleet" label="The jets' names (one per line)">
                <Textarea
                  id="air-fleet"
                  rows={3}
                  value={airport.airline.fleet.join('\n')}
                  onChange={(e) =>
                    change(
                      (draft) =>
                        void (draft.airline.fleet = e.target.value
                          .split('\n')
                          .map((line) => line.trim())
                          .filter(Boolean)
                          .slice(0, 12)),
                    )
                  }
                />
              </Field>
              <Field id="air-soon" label="Told at the desk while no city is open">
                <Input
                  id="air-soon"
                  value={airport.soonNote}
                  maxLength={160}
                  onChange={(e) => change((draft) => void (draft.soonNote = e.target.value))}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Cabins">
            <h2 className="content-subtitle">Cabins</h2>
            <p className="admin-muted">Economy is the base fare; the others are so many times it.</p>
            {CABIN_KEYS.map((key) => {
              const cabin = airport.cabins[key];
              return (
                <div className="content-grid" key={key} data-testid={`cabin-${key}`}>
                  <Field id={`cabin-${key}-label`} label={`${key}: name`}>
                    <Input
                      id={`cabin-${key}-label`}
                      value={cabin.label}
                      maxLength={30}
                      onChange={(e) => change((draft) => void (draft.cabins[key].label = e.target.value))}
                    />
                  </Field>
                  <Field id={`cabin-${key}-times`} label="Times economy">
                    <Input
                      id={`cabin-${key}-times`}
                      type="number"
                      min={1}
                      step="0.1"
                      value={cabin.times}
                      disabled={key === 'economy'}
                      onChange={(e) =>
                        change((draft) => void (draft.cabins[key].times = number(e.target.value, cabin.times)))
                      }
                    />
                  </Field>
                  <Field id={`cabin-${key}-perk`} label="What you get">
                    <Input
                      id={`cabin-${key}-perk`}
                      value={cabin.perk}
                      maxLength={120}
                      onChange={(e) => change((draft) => void (draft.cabins[key].perk = e.target.value))}
                    />
                  </Field>
                  <Field id={`cabin-${key}-seat`} label="The seat">
                    <Input
                      id={`cabin-${key}-seat`}
                      value={cabin.seat}
                      maxLength={40}
                      onChange={(e) => change((draft) => void (draft.cabins[key].seat = e.target.value))}
                    />
                  </Field>
                </div>
              );
            })}
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Airports">
            <h2 className="content-subtitle">The game's airports</h2>
            <p className="admin-muted">
              Flights go between these. The fare is an economy seat to fly there; peak hours and the cabin add to it.
            </p>
            {airportsOf(airport).map((item) => (
              <div className="content-grid content-row" key={item.placeId} data-testid={`airport-${item.placeId}`}>
                <Field id={`stop-${item.placeId}-name`} label="Name">
                  <Input
                    id={`stop-${item.placeId}-name`}
                    value={item.name}
                    maxLength={40}
                    onChange={(e) => stop(item.placeId, (entry) => void (entry.name = e.target.value))}
                  />
                </Field>
                <Field id={`stop-${item.placeId}-code`} label="Code">
                  <Input
                    id={`stop-${item.placeId}-code`}
                    value={item.code}
                    maxLength={3}
                    onChange={(e) => stop(item.placeId, (entry) => void (entry.code = e.target.value.toUpperCase()))}
                  />
                </Field>
                <Field id={`stop-${item.placeId}-fare`} label="Economy fare (£)">
                  <Input
                    id={`stop-${item.placeId}-fare`}
                    type="number"
                    min={1}
                    step="0.01"
                    value={item.farePence / 100}
                    onChange={(e) =>
                      stop(item.placeId, (entry) => void (entry.farePence = toPence(e.target.value, item.farePence)))
                    }
                  />
                </Field>
                <Field id={`stop-${item.placeId}-secs`} label="Flight (seconds)">
                  <Input
                    id={`stop-${item.placeId}-secs`}
                    type="number"
                    min={10}
                    max={300}
                    value={item.flightSeconds}
                    onChange={(e) =>
                      stop(
                        item.placeId,
                        (entry) => void (entry.flightSeconds = Math.round(number(e.target.value, item.flightSeconds))),
                      )
                    }
                  />
                </Field>
                <Field id={`stop-${item.placeId}-gates`} label="Gates">
                  <Input
                    id={`stop-${item.placeId}-gates`}
                    type="number"
                    min={1}
                    max={40}
                    value={item.gates}
                    onChange={(e) =>
                      stop(item.placeId, (entry) => void (entry.gates = Math.round(number(e.target.value, item.gates))))
                    }
                  />
                </Field>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`Flights to ${item.name}`}
                    onCheckedChange={(on) => stop(item.placeId, (entry) => void (entry.visible = on))}
                  />{' '}
                  Flights running
                </label>
              </div>
            ))}
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Bag cover">
            <h2 className="content-subtitle">Bag cover</h2>
            <div className="content-grid">
              <Field id="cover-price" label="Price (£)">
                <Input
                  id="cover-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={cover.pence / 100}
                  onChange={(e) => changeCover((draft) => void (draft.pence = toPence(e.target.value, cover.pence)))}
                />
              </Field>
              <Field id="cover-loss" label="A bag goes missing (% of flights)">
                <Input
                  id="cover-loss"
                  type="number"
                  min={0}
                  max={50}
                  value={cover.lossPercent}
                  onChange={(e) =>
                    changeCover(
                      (draft) =>
                        void (draft.lossPercent = Math.max(
                          0,
                          Math.min(50, Math.round(number(e.target.value, cover.lossPercent))),
                        )),
                    )
                  }
                />
              </Field>
              <Field id="cover-fun" label="Not covered: fun lost">
                <Input
                  id="cover-fun"
                  type="number"
                  min={0}
                  max={100}
                  value={-(cover.lostEffects.fun ?? 0)}
                  onChange={(e) =>
                    changeCover(
                      (draft) =>
                        void (draft.lostEffects = {
                          ...draft.lostEffects,
                          fun: -Math.max(0, Math.min(100, Math.round(number(e.target.value, 0)))),
                        }),
                    )
                  }
                />
              </Field>
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Bring someone along">
            <h2 className="content-subtitle">Bring someone along</h2>
            <p className="admin-muted">
              A flyer offers seats to friends, partners and family at the gate; they buy their own ticket and the jet
              waits for them.
            </p>
            <div className="content-grid">
              <Field id="along-seats" label="Seats a flyer can offer">
                <Input
                  id="along-seats"
                  type="number"
                  min={1}
                  max={8}
                  value={along.seats}
                  onChange={(e) =>
                    changeAlong(
                      (draft) =>
                        void (draft.seats = Math.max(1, Math.min(8, Math.round(number(e.target.value, along.seats))))),
                    )
                  }
                />
              </Field>
              <Field id="along-hold" label="The jet waits for them (seconds)">
                <Input
                  id="along-hold"
                  type="number"
                  min={10}
                  max={600}
                  value={along.holdSeconds}
                  onChange={(e) =>
                    changeAlong(
                      (draft) =>
                        void (draft.holdSeconds = Math.max(
                          10,
                          Math.min(600, Math.round(number(e.target.value, along.holdSeconds))),
                        )),
                    )
                  }
                />
              </Field>
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Private jets">
            <h2 className="content-subtitle">Private jets</h2>
            <p className="admin-muted">
              Departures&apos; My jet: a player buys a jet, it parks on the private apron with their name on it, and
              they fly it to the other airports for its fuel. No boarding.
            </p>
            <div className="content-grid">
              <Field id="jets-secs" label="A trip (seconds)">
                <Input
                  id="jets-secs"
                  type="number"
                  min={5}
                  max={120}
                  value={jets.seconds}
                  onChange={(e) =>
                    changeJets(
                      (draft) =>
                        void (draft.seconds = Math.max(
                          5,
                          Math.min(120, Math.round(number(e.target.value, jets.seconds))),
                        )),
                    )
                  }
                />
              </Field>
              <Field id="jets-sell" label="Sold back for (% of the price)">
                <Input
                  id="jets-sell"
                  type="number"
                  min={0}
                  max={100}
                  value={Math.round(jets.sellShare * 100)}
                  onChange={(e) =>
                    changeJets(
                      (draft) =>
                        void (draft.sellShare =
                          Math.max(0, Math.min(100, Math.round(number(e.target.value, jets.sellShare * 100)))) / 100),
                    )
                  }
                />
              </Field>
              {(['body', 'tail', 'stripe'] as const).map((part) => (
                <Field key={part} id={`jets-${part}`} label={`Paint: ${part}`}>
                  <Input
                    id={`jets-${part}`}
                    type="color"
                    value={jets.colours[part]}
                    onChange={(e) => changeJets((draft) => void (draft.colours[part] = e.target.value))}
                  />
                </Field>
              ))}
            </div>
            {JET_KINDS.map((kind) => {
              const model = jets.kinds[kind];
              return (
                <div className="content-grid" key={kind}>
                  <Field id={`jet-${kind}-label`} label={`${kind}: name`}>
                    <Input
                      id={`jet-${kind}-label`}
                      value={model.label}
                      maxLength={30}
                      onChange={(e) => changeJets((draft) => void (draft.kinds[kind].label = e.target.value))}
                    />
                  </Field>
                  <Field id={`jet-${kind}-price`} label="Price (£)">
                    <Input
                      id={`jet-${kind}-price`}
                      type="number"
                      min={1}
                      value={model.pricePence / 100}
                      onChange={(e) =>
                        changeJets(
                          (draft) =>
                            void (draft.kinds[kind].pricePence = Math.max(
                              100,
                              toPence(e.target.value, model.pricePence),
                            )),
                        )
                      }
                    />
                  </Field>
                  <Field id={`jet-${kind}-fuel`} label="Fuel a trip (£)">
                    <Input
                      id={`jet-${kind}-fuel`}
                      type="number"
                      min={0.01}
                      step="0.01"
                      value={model.fuelPence / 100}
                      onChange={(e) =>
                        changeJets(
                          (draft) =>
                            void (draft.kinds[kind].fuelPence = Math.max(1, toPence(e.target.value, model.fuelPence))),
                        )
                      }
                    />
                  </Field>
                  <Field id={`jet-${kind}-blurb`} label="What it is">
                    <Input
                      id={`jet-${kind}-blurb`}
                      value={model.blurb}
                      maxLength={120}
                      onChange={(e) => changeJets((draft) => void (draft.kinds[kind].blurb = e.target.value))}
                    />
                  </Field>
                </div>
              );
            })}
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="VIP chopper">
            <h2 className="content-subtitle">VIP chopper</h2>
            <p className="admin-muted">
              Departures&apos; Chopper: from the airport&apos;s helipad straight to the door of any place on the map. In
              the landing line, {'{place}'} is where it lands.
            </p>
            <div className="content-grid">
              <Field id="chopper-fare" label="Fare (£)">
                <Input
                  id="chopper-fare"
                  type="number"
                  min={1}
                  step="0.01"
                  value={chopper.farePence / 100}
                  onChange={(e) =>
                    changeChopper(
                      (draft) => void (draft.farePence = Math.max(100, toPence(e.target.value, chopper.farePence))),
                    )
                  }
                />
              </Field>
              <Field id="chopper-secs" label="A ride (seconds)">
                <Input
                  id="chopper-secs"
                  type="number"
                  min={5}
                  max={120}
                  value={chopper.seconds}
                  onChange={(e) =>
                    changeChopper(
                      (draft) =>
                        void (draft.seconds = Math.max(
                          5,
                          Math.min(120, Math.round(number(e.target.value, chopper.seconds))),
                        )),
                    )
                  }
                />
              </Field>
              {(['lift', 'over', 'down'] as const).map((line) => (
                <Field key={line} id={`chopper-${line}`} label={`Line: ${line}`}>
                  <Input
                    id={`chopper-${line}`}
                    value={chopper.lines[line]}
                    maxLength={120}
                    onChange={(e) => changeChopper((draft) => void (draft.lines[line] = e.target.value))}
                  />
                </Field>
              ))}
              <label className="designer-check">
                <Switch
                  checked={chopper.visible}
                  aria-label="Choppers flying"
                  onCheckedChange={(on) => changeChopper((draft) => void (draft.visible = on))}
                />{' '}
                Choppers flying
              </label>
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="On board">
            <h2 className="content-subtitle">On board</h2>
            <p className="admin-muted">
              What the flight's card says. Lines during the flight: how far through (0 to 1), a bar, then the words. In
              the landing line, {'{airport}'} is the airport landed at.
            </p>
            <div className="content-grid">
              <Field id="script-boarding" label="Boarding">
                <Input
                  id="script-boarding"
                  value={script.boarding}
                  maxLength={120}
                  onChange={(e) => changeScript((draft) => void (draft.boarding = e.target.value))}
                />
              </Field>
              <Field id="script-takeoff" label="Take-off">
                <Input
                  id="script-takeoff"
                  value={script.takeOff}
                  maxLength={120}
                  onChange={(e) => changeScript((draft) => void (draft.takeOff = e.target.value))}
                />
              </Field>
              <Field id="script-landed" label="Landed">
                <Input
                  id="script-landed"
                  value={script.landed}
                  maxLength={120}
                  onChange={(e) => changeScript((draft) => void (draft.landed = e.target.value))}
                />
              </Field>
              <Field id="script-lines" label="During the flight (one per line: 0.5 | Cruising at 35,000 ft)">
                <Textarea
                  id="script-lines"
                  rows={6}
                  // edited freely, read when the field is left (a half-typed line is not lost)
                  key={JSON.stringify(script.lines)}
                  defaultValue={script.lines.map((line) => `${line.at} | ${line.text}`).join('\n')}
                  onBlur={(e) =>
                    changeScript(
                      (draft) =>
                        void (draft.lines = e.target.value
                          .split('\n')
                          .map((line) => line.match(/^\s*(0(?:\.\d+)?|1(?:\.0+)?)\s*\|\s*(.+)$/))
                          .filter((match): match is RegExpMatchArray => Boolean(match))
                          .map((match) => ({ at: Number(match[1]), text: match[2]!.trim().slice(0, 120) }))
                          .slice(0, 12)),
                    )
                  }
                />
              </Field>
            </div>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Destinations">
            <h2 className="content-subtitle">Cities opening soon</h2>
            <p className="admin-muted">
              Shown on Departures as “opens soon” until the city is built. The fare is an economy seat; peak hours below
              add to it.
            </p>
            {airport.destinations.map((item) => (
              <div className="content-grid content-row" key={item.id} data-testid={`destination-${item.id}`}>
                <Field id={`dest-${item.id}-name`} label="City">
                  <Input
                    id={`dest-${item.id}-name`}
                    value={item.name}
                    maxLength={40}
                    onChange={(e) => destination(item.id, (entry) => void (entry.name = e.target.value))}
                  />
                </Field>
                <Field id={`dest-${item.id}-icon`} label="Icon">
                  <Input
                    id={`dest-${item.id}-icon`}
                    value={item.icon}
                    maxLength={16}
                    onChange={(e) => destination(item.id, (entry) => void (entry.icon = e.target.value))}
                  />
                </Field>
                <Field id={`dest-${item.id}-airport`} label="Airport">
                  <Input
                    id={`dest-${item.id}-airport`}
                    value={item.airport}
                    maxLength={60}
                    onChange={(e) => destination(item.id, (entry) => void (entry.airport = e.target.value))}
                  />
                </Field>
                <Field id={`dest-${item.id}-code`} label="Code">
                  <Input
                    id={`dest-${item.id}-code`}
                    value={item.code}
                    maxLength={3}
                    onChange={(e) => destination(item.id, (entry) => void (entry.code = e.target.value.toUpperCase()))}
                  />
                </Field>
                <Field id={`dest-${item.id}-fare`} label="Economy fare (£)">
                  <Input
                    id={`dest-${item.id}-fare`}
                    type="number"
                    min={1}
                    step="0.01"
                    value={item.farePence / 100}
                    onChange={(e) =>
                      destination(item.id, (entry) => void (entry.farePence = toPence(e.target.value, item.farePence)))
                    }
                  />
                </Field>
                <Field id={`dest-${item.id}-secs`} label="Flight (seconds)">
                  <Input
                    id={`dest-${item.id}-secs`}
                    type="number"
                    min={5}
                    max={600}
                    value={item.flightSeconds}
                    onChange={(e) =>
                      destination(
                        item.id,
                        (entry) => void (entry.flightSeconds = Math.round(number(e.target.value, item.flightSeconds))),
                      )
                    }
                  />
                </Field>
                <label className="designer-check">
                  <Switch
                    checked={item.visible}
                    aria-label={`${item.name} on the board`}
                    onCheckedChange={(on) => destination(item.id, (entry) => void (entry.visible = on))}
                  />{' '}
                  On the board
                </label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    change((draft) => void (draft.destinations = draft.destinations.filter((d) => d.id !== item.id)))
                  }
                >
                  Remove
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={airport.destinations.length >= 20}
              onClick={() =>
                change((draft) => {
                  const id = idFor('New city', new Set(draft.destinations.map((d) => d.id)), 'city');
                  draft.destinations.push({
                    id,
                    name: 'New city',
                    icon: '🏙️',
                    airport: 'Its airport',
                    code: 'XXX',
                    farePence: 9_900,
                    flightSeconds: 30,
                    visible: false,
                  });
                })
              }
            >
              + Add a destination
            </Button>
          </fieldset>

          <fieldset className="admin-panel content-section" disabled={readOnly} aria-label="Peak hours">
            <h2 className="content-subtitle">Peak hours and boarding</h2>
            <div className="content-grid">
              <Field id="peak-times" label="Fares at peak, times">
                <Input
                  id="peak-times"
                  type="number"
                  min={1}
                  max={5}
                  step="0.1"
                  value={airport.peak.times}
                  onChange={(e) =>
                    change((draft) => void (draft.peak.times = number(e.target.value, airport.peak.times)))
                  }
                />
              </Field>
              <Field id="peak-hours" label="Peak hours (London time, from-to, one per line)">
                <Textarea
                  id="peak-hours"
                  rows={2}
                  value={airport.peak.hours.map(([from, to]) => `${from}-${to}`).join('\n')}
                  onChange={(e) =>
                    change(
                      (draft) =>
                        void (draft.peak.hours = e.target.value
                          .split('\n')
                          .map((line) => line.trim().match(/^(\d{1,2})\s*-\s*(\d{1,2})$/))
                          .filter((match): match is RegExpMatchArray => Boolean(match))
                          .map((match) => [Number(match[1]), Number(match[2])] as [number, number])
                          .slice(0, 4)),
                    )
                  }
                />
              </Field>
              <Field id="boarding" label="Boarding (seconds)">
                <Input
                  id="boarding"
                  type="number"
                  min={0}
                  max={300}
                  value={airport.boardingSeconds}
                  onChange={(e) =>
                    change(
                      (draft) =>
                        void (draft.boardingSeconds = Math.round(number(e.target.value, airport.boardingSeconds))),
                    )
                  }
                />
              </Field>
            </div>
          </fieldset>
        </div>
      )}
    </section>
  );
}
